import { z } from 'zod';

import { ZodCurrency } from '@Common/types/Amount';
import { ZodInvestmentAccountB2CTypeEnum } from '@Common/types/profile/me';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export type UserCurrency = z.TypeOf<typeof ZodCurrency>;

export const ZodUserPortfolioPosition = z.object({
  id: ZodId,
  symbol: ZodTrimmedString,
  stock_name: ZodTrimmedString,
  qty: z.number(),
  total_gain: z.number(),
  unrealized_plpc: z.number().nullable(),
  total_invested: z.number(),
  average_cost: z.number(),
  market_value_user_currency: z.number(),
  currency: ZodCurrency,
  average_cost_currency: ZodCurrency,
  gain_loss: z.number().optional(),
  fx_rate_impact: z.number().optional(),
});
export type UserPortfolioPosition = z.TypeOf<typeof ZodUserPortfolioPosition>;

export const ZodUserPortfolioTotal = z.object({
  total_invested: z.number(),
  total_gain: z.number(),
  currency: ZodCurrency,
  gain_loss: z.number().optional(),
  fx_rate_impact: z.number().optional(),
});
export type UserPortfolioTotal = z.TypeOf<typeof ZodUserPortfolioTotal>;

const ZodUserPortfolioOthers = z.array(
  z.object({
    qty: z.number(),
    symbol: ZodTrimmedString,
    instrument_id: ZodId,
  }),
);
export type UserPortfolioOthers = z.TypeOf<typeof ZodUserPortfolioOthers>;

const ZodUserPortfolio = z.object({
  positions: z.array(ZodUserPortfolioPosition),
  total: ZodUserPortfolioTotal,
});
export type UserPortfolio = z.TypeOf<typeof ZodUserPortfolio>;

const ZodGetUserPortfolioResponse = z.object({
  id: ZodId,
  stock: ZodUserPortfolio,
  crypto: ZodUserPortfolio.nullable(),
  mutual_fund: ZodUserPortfolio.nullable(),
  other: ZodUserPortfolio,
});
export type GetUserPortfolioResponse = z.TypeOf<typeof ZodGetUserPortfolioResponse>;

export const getUsersPortfolioIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  queryParams: createZodParamsValidator(
    z.object({
      investment_account_type: ZodInvestmentAccountB2CTypeEnum.default('gia'),
    }),
  ),
  responseBody: ZodGetUserPortfolioResponse,
});

export type GetUsersPortfolioIdEndpoint = GetEndpointType<typeof getUsersPortfolioIdEndpoint>;
